import { useEffect, useState } from "react";
import { Trophy, Camera, AlertTriangle, Star, Users, Award } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const MEDAL_COLOR = { gold: "#d4af37", silver: "#c0c0c0", bronze: "#cd7f32" };
const MEDAL_LABEL = { gold: "1º", silver: "2º", bronze: "3º" };

/**
 * /admin/equipa — last-7-days leaderboard for cleaning crew.
 * Sort = derived score (cleanings × 3 + photos × 0.5 + (avg_nps − 5) − issues × 0.5).
 * Top 3 with cleanings > 0 get gold/silver/bronze badges.
 */
export default function AdminTeam() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/team/weekly");
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="text-[#a0b2af]">A carregar leaderboard…</div>;

  const rows = data?.rows || [];
  const period = data?.period || {};
  const top = rows.find(r => r.badge === "gold" && r.cleanings_7d > 0);

  return (
    <div className="space-y-8" data-testid="admin-team">
      <div>
        <div className="eyebrow mb-3 flex items-center gap-2">
          <Trophy size={12} /> Equipa da semana · {period.since} → {period.until}
        </div>
        <h1 className="font-display text-5xl">Performance da equipa.</h1>
      </div>

      {top && (
        <div className="lux-card p-6 lg:p-8 border-[#d4af37] bg-gradient-to-r from-[#d4af37]/10 to-transparent flex items-center gap-5"
             data-testid="team-spotlight">
          <div className="w-16 h-16 rounded-full bg-[#d4af37] flex items-center justify-center text-[#031c18] shrink-0">
            <Trophy size={28} />
          </div>
          <div className="flex-1">
            <div className="eyebrow text-[#d4af37] mb-1">Funcionário em destaque</div>
            <div className="font-display text-3xl">{top.name}</div>
            <div className="text-sm text-[#a0b2af] mt-1">
              {top.cleanings_7d} limpezas · {top.photos_7d} fotos
              {top.nps_count > 0 && ` · NPS ${top.avg_nps.toFixed(1)}/10`}
            </div>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="lux-card p-12 text-center text-[#a0b2af] italic" data-testid="team-empty">
          Sem funcionários ativos para apresentar.
        </div>
      ) : (
        <div className="lux-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#062c27] text-[10px] uppercase tracking-widest text-[#a0b2af]">
              <tr>
                <th className="text-left px-4 py-3">Posição</th>
                <th className="text-left px-4 py-3">Funcionário</th>
                <th className="text-center px-3 py-3"><Camera size={12} className="inline" /> Limpezas (7d)</th>
                <th className="text-center px-3 py-3"><Camera size={12} className="inline" /> Fotos</th>
                <th className="text-center px-3 py-3"><AlertTriangle size={12} className="inline" /> Problemas</th>
                <th className="text-center px-3 py-3"><Star size={12} className="inline" /> NPS médio</th>
                <th className="text-right px-4 py-3">Score</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} className="border-t border-[#1a4640]"
                    data-testid={`team-row-${r.id}`}>
                  <td className="px-4 py-3">
                    {r.badge ? (
                      <span className="inline-flex items-center gap-2 font-display text-lg"
                            style={{ color: MEDAL_COLOR[r.badge] }}>
                        <Award size={14} /> {MEDAL_LABEL[r.badge]}
                      </span>
                    ) : (
                      <span className="text-[#a0b2af]">{i + 1}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-display">{r.name}</div>
                    <div className="text-[11px] text-[#a0b2af]">{r.email}</div>
                  </td>
                  <td className="text-center px-3 py-3 font-display text-lg text-[#d4af37]">
                    {r.cleanings_7d}
                  </td>
                  <td className="text-center px-3 py-3 text-[#a0b2af]">{r.photos_7d}</td>
                  <td className={`text-center px-3 py-3 ${r.issues_7d > 0 ? "text-[#f59e0b]" : "text-[#a0b2af]"}`}>
                    {r.issues_7d}
                  </td>
                  <td className="text-center px-3 py-3">
                    {r.nps_count > 0 ? (
                      <span className="text-[#10b981]">
                        {r.avg_nps.toFixed(1)}
                        <span className="text-[10px] text-[#a0b2af] ml-1">({r.nps_count})</span>
                      </span>
                    ) : (
                      <span className="text-[#a0b2af]">—</span>
                    )}
                  </td>
                  <td className="text-right px-4 py-3 font-display text-[#d4af37]">
                    {r.score.toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="lux-card p-5 text-xs text-[#a0b2af] flex items-start gap-3">
        <Users size={14} className="text-[#d4af37] shrink-0 mt-0.5" />
        <div>
          O <strong className="text-[#fdfbf7]">score</strong> é calculado como
          <span className="text-[#d4af37]"> limpezas × 3 + fotos × 0.5 + (NPS médio − 5) − problemas × 0.5</span>.
          Mantém o ranking justo entre quem limpa muito e quem limpa pouco mas com excelência.
        </div>
      </div>
    </div>
  );
}
